/**
 * SFX Catalog (Fase 0)
 *
 * "Martelados" — short, synthesized one-shots. Each entry is a recipe that
 * plays into the supplied AudioContext + destination GainNode and returns
 * the time (in seconds, ctx absolute time) when the SFX fully finishes —
 * useful if the caller wants to chain animations to the audio.
 *
 * To add a new SFX:
 *   1. Add the id to the SfxId union below.
 *   2. Add a recipe to SFX_RECIPES.
 *   3. (Optional) Add a documentation entry in the table in
 *      docs/WIP/PLANO_SISTEMA_SOM.md §5.2.
 *
 * Why synth-only for Fase 0:
 *   - No asset hosting / bandwidth cost.
 *   - No licensing concerns.
 *   - Instant: no preloading latency.
 *   - Trivial to A/B: tweak numbers, hear immediately.
 *   Fase 1+ can add `playFile(url)` for when we want real samples.
 */

import { playSweep, playTone, makeLowpass } from './synth';

export type SfxId =
  | 'episode_start'
  | 'episode_end'
  | 'episode_complete'
  | 'success'
  | 'error'
  | 'click';

export type SfxRecipe = (
  ctx: AudioContext,
  /** Where the SFX connects in the audio graph. */
  dest: GainNode,
  /** Per-call volume scalar (0..1). The SFX layer also has a master gain. */
  volume: number,
) => number; // returns ctx time when SFX finishes

// ── Recipes ────────────────────────────────────────────────────────────────

/**
 * episode_start — confident rising sweep. "Begin!"
 * C3 → A4, sine, 220ms.
 */
const episodeStart: SfxRecipe = (ctx, dest, vol) => {
  return playSweep(ctx, dest, {
    fromHz: 130, // C3
    toHz: 440, // A4
    duration: 0.22,
    type: 'sine',
    peak: 0.28 * vol,
  });
};

/**
 * episode_end — bell-like two-note resolution. "Chapter closed."
 * C5 then E5, sine, 150ms each, 30ms gap.
 */
const episodeEnd: SfxRecipe = (ctx, dest, vol) => {
  const t0 = playTone(ctx, dest, {
    freq: 523.25, // C5
    duration: 0.18,
    type: 'sine',
    peak: 0.25 * vol,
  });
  return playTone(ctx, dest, {
    freq: 659.25, // E5
    startAt: t0 + 0.03,
    duration: 0.32,
    type: 'sine',
    peak: 0.25 * vol,
  });
};

/**
 * episode_complete — full triad arpeggio (C-E-G), short, staccato.
 * "Adventure complete!" celebratory.
 */
const episodeComplete: SfxRecipe = (ctx, dest, vol) => {
  const notes = [523.25, 659.25, 783.99]; // C5 E5 G5
  let t = ctx.currentTime;
  for (const freq of notes) {
    t = playTone(ctx, dest, {
      freq,
      startAt: t,
      duration: 0.13,
      type: 'triangle',
      peak: 0.22 * vol,
    });
    t += 0.025; // gap between notes
  }
  // final high note, longer — the "ta-da!"
  return playTone(ctx, dest, {
    freq: 1046.5, // C6
    startAt: t + 0.03,
    duration: 0.45,
    type: 'triangle',
    peak: 0.25 * vol,
  });
};

/**
 * success — short bright pluck, a single note. "Saved / done."
 * Goes through a lowpass for a soft, warm timbre.
 */
const success: SfxRecipe = (ctx, dest, vol) => {
  const lp = makeLowpass(ctx, 1800, 0.7);
  // Re-route: tone → lowpass → dest. Detour through a tap gain.
  const tap = ctx.createGain();
  tap.gain.value = 1;
  lp.connect(tap);
  tap.connect(dest);
  return playTone(ctx, lp, {
    freq: 880, // A5
    duration: 0.16,
    type: 'sine',
    peak: 0.3 * vol,
  });
};

/**
 * error — low buzzer, two short pulses. "Try again."
 * Square wave at 110Hz, 80ms on / 40ms gap / 80ms on.
 */
const error: SfxRecipe = (ctx, dest, vol) => {
  const t0 = playTone(ctx, dest, {
    freq: 110,
    duration: 0.08,
    type: 'square',
    peak: 0.18 * vol,
  });
  return playTone(ctx, dest, {
    freq: 110,
    startAt: t0 + 0.04,
    duration: 0.14,
    type: 'square',
    peak: 0.18 * vol,
  });
};

/**
 * click — very soft tick for non-critical UI interactions.
 * Plucked sine at 1.2kHz, 30ms — below threshold of "noticeable".
 */
const click: SfxRecipe = (ctx, dest, vol) => {
  return playTone(ctx, dest, {
    freq: 1200,
    duration: 0.03,
    type: 'sine',
    peak: 0.12 * vol,
  });
};

// ── Public table ──────────────────────────────────────────────────────────

export const SFX_RECIPES: Record<SfxId, SfxRecipe> = {
  episode_start: episodeStart,
  episode_end: episodeEnd,
  episode_complete: episodeComplete,
  success: success,
  error: error,
  click: click,
};

/**
 * Human-readable labels for the optional SFX mute toggle UI.
 * Keep these short — they render in a pill.
 */
export const SFX_LABELS: Record<SfxId, string> = {
  episode_start: 'Início do episódio',
  episode_end: 'Fim do episódio',
  episode_complete: 'Aventura completa',
  success: 'Sucesso',
  error: 'Erro',
  click: 'Clique',
};
